import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { usePaymentForm } from '../../hooks/usePaymentForm';
import AmountForm from './AmountForm';
import CardForm from './CardForm';
import AmountBreakdown from './AmountBreakdown';

export default function PaymentModal({
  isOpen,
  onClose,
  amount,
  onSuccess
}) {
  const [showCardForm, setShowCardForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(amount);
  const [configurationError, setConfigurationError] = useState('');
  const [session, setSession] = useState(null);
  const [threeDSecureHtml, setThreeDSecureHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false); // 🔹 NEW LOADING STATE
  
  const {
    isProcessing,
    error,
    sessionId,
    initializePayment,
    finalizePayment,
    cleanup
  } = usePaymentForm({
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
    onError: () => {
      setConfigurationError('Unable to process payment. Please try again later.');
    }
  });

  useEffect(() => {
    setPaymentAmount(amount);
  }, [amount]);

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen]);

  const handleAmountSubmit = async (amount) => {
    setConfigurationError('');
    setPaymentAmount(amount);

    try {
      const sessionData = await initializePayment(amount);
      setSession(sessionData);
      setShowCardForm(true);
    } catch (err) {
      setConfigurationError('Unable to process payment. Please try again later.');
    }
  };

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConfigurationError("");
    setThreeDSecureHtml("");
    setIsLoading(true); // 🔹 SHOW LOADING

    try {
      const response = await finalizePayment();

      if (response.data.message === "needs_auth" && response.data.html) {
        setThreeDSecureHtml(response.data.html);
      } else if (response.data.message === "successful") {
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      setConfigurationError("Unable to process payment. Please try again later.");
    } finally {
      setIsLoading(false); // 🔹 HIDE LOADING
    }
  };

  const handleClose = () => {
    setShowCardForm(false);
    setConfigurationError('');
    setSession(null);
    setThreeDSecureHtml('');
    // cleanup();
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                    {/* {showCardForm ? 'Enter Card Details' : 'Payment Amount'} */}
                    {showCardForm ? 'Please wait ...' : ''}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <FiX className="h-5 w-5" />
                  </button>
                </div>

                {(error || configurationError) && (
                  <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm text-center">
                    Unable to process payment. Please try again later.
                  </div>
                )}

                {isLoading ? (
                  <div className="flex items-center justify-center h-[200px]">
                    <p className="text-gray-600 dark:text-gray-300">Loading OTP Verification...</p>
                  </div>
                ) : threeDSecureHtml ? (
                  <div className="w-full h-[500px] relative">
                    {/* Loader while waiting for iframe content */}
                    {!threeDSecureHtml.includes("<iframe") && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <p className="text-gray-600 dark:text-gray-300">Loading OTP Verification...</p>
                      </div>
                    )}

                    <div
                      className="w-full h-full"
                      ref={(ref) => {
                        if (ref) {
                          ref.innerHTML = threeDSecureHtml;

                          setTimeout(() => {
                            const scriptTag = ref.querySelector("#authenticate-payer-script");
                            if (scriptTag) {
                              const newScript = document.createElement("script");
                              newScript.textContent = scriptTag.textContent;
                              document.body.appendChild(newScript);
                              document.body.removeChild(newScript);
                            }
                          }, 300);
                        }
                      }}
                    />
                  </div>
                ) : !showCardForm ? (
                  <AmountForm
                    initialAmount={paymentAmount}
                    isProcessing={isProcessing}
                    onSubmit={handleAmountSubmit}
                    onCancel={handleClose}
                  />
                ) : session ? (
                  <>
                    <AmountBreakdown
                      amount={session.amount}
                      processingFee={session.processingFee}
                      totalAmount={session.totalAmount}
                    />
                    <CardForm
                      amount={session.totalAmount}
                      sessionId={session.sessionId}
                      isProcessing={isProcessing}
                      onSubmit={handleCardSubmit}
                      onCancel={() => setShowCardForm(false)}
                    />
                  </>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}